import React, { FC, memo, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { FeatureLayerServiceInfoDc, GeometryType } from "@evergis/api";
import { Checkbox } from "@evergis/ui";
import { Legend } from "@evergis/react";

import { StyleIcon } from "components/StyleIcon/StyleIcon";
import { returnFound } from "find-and";
import { FeatureCreateButton } from "./Buttons/FeatureCreateButton";
import { RefreshButton } from "./Buttons/RefreshButton";
import { TempLayerBadge } from "./Buttons/TempLayerBadge";
import { VisibilityButton } from "./Buttons/VisibilityButton";
import { ItemTitle } from "./ItemTitle";
import { ListMenu } from "./ListMenu/ListMenu";
import { Content, Control, IconContainer, LayerIconContainer, Main, Section, StyledMapLegend } from "../styled";
import { isEditable, isEditableDefinition } from "../../../evergis/core/permissions";
import { isErrorStatus } from "../../../resources/layerWithTable";
import { goToStyle as goToStyleAction } from "../../../ducks/location/location";
import { getExtractedSymbol } from "../../../utils/style";
import { getHiddenLegends } from "../../../ducks/style/selectors";
import {
  clearHiddenLegends as clearHiddenLegendsAction,
  toggleHiddenLegend as toggleHiddenLegendAction,
} from "../../../ducks/style/style";
import { formatAttributeValue } from "../../../utils/formatAttributeValue";
import { isClusterSymbol } from "../../../interfaces/style";
import { useOpenStyle } from "../hooks/useOpenStyle";
import { useAction } from "../../../hooks/useAction";
import { useProjectLayers } from "../../../hooks/project/useProjectLayers";
import { useLegendExpand } from "../hooks/useLegendExpand";
import { useIsShared } from "../../../hooks/api/useIsShared";
import { SGisPolygonSymbol } from "../../../react-sgis/symbols/SGisPolygonSymbol";
import { LayerItemProps } from "../types";

export const LayerItem: FC<LayerItemProps> = memo(
  // eslint-disable-next-line max-lines-per-function
  ({
    layer: layerItem,
    isDragging,
    isExpanding,
    noMenu,
    noControl,
    noLegend,
    noIcon,
    noVisibilityClick,
    layerItemInfo,
    isOutOfScale,
    isSelectable,
    isSelected,
    toggleSelected,
  }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation("layerList");
    const hiddenLegends = useSelector(getHiddenLegends);
    const isShared = useIsShared();
    const projectLayers = useProjectLayers();
    const { name, opacity, children, path, isVisible, isLegendExpanded } = layerItem;
    const { layer, segmentStyle: style, table, refetch, status, loading } = layerItemInfo || {};
    const { geometryType, type, sourceType, permissions } = layer || {};
    const [expanded, toggleExpanded] = useLegendExpand(name, isLegendExpanded);

    const projectLayer = useMemo(
      () => (layer?.name ? returnFound(projectLayers, { name: layer?.name }) : null),
      [layer?.name, projectLayers],
    );

    const isCluster = isClusterSymbol(style?.symbol);
    const extracted = getExtractedSymbol(style?.symbol, true);
    const originalSymbol = isCluster ? style.symbol : (extracted as SGisPolygonSymbol)?.originalSymbol || extracted;

    const isEditableLayer = isEditable(permissions);
    const isEditableTable = table && isEditable(table.permissions);
    const isEditableType =
      layer?.type === "QueryLayerService" ||
      (isEditableTable && isEditableDefinition(layer) && geometryType !== GeometryType.Unknown);
    const isRaster = type === "PostgresTileLayerService";

    const symbolHasRasters = style && style.hasRasters();
    const symbolIsClassified = style && style.classificationManager.hasClassifications();
    const ableToOpenClassification = isEditableLayer && !noControl && !noMenu && !isSelectable && !status;

    const isTemp = name?.startsWith("temp");

    const openStyle = useOpenStyle(name);

    const openClassification = useCallback(
      () => ableToOpenClassification && openStyle(),
      [ableToOpenClassification, openStyle],
    );

    const toggleHiddenLegend = useCallback(
      (layerName: string, hiddenCondition: string) => {
        dispatch(toggleHiddenLegendAction({ layerName, hiddenCondition }));
      },
      [dispatch],
    );

    const clearHiddenLegends = useAction(clearHiddenLegendsAction);
    const goToStyle = useAction(goToStyleAction);

    const isActiveStyle = isVisible && ableToOpenClassification;

    const renderIcon = useMemo(
      () =>
        noIcon ? null : (
          <LayerIconContainer
            title={isActiveStyle ? t("style") : null}
            onClick={openClassification}
            halfVisible={isOutOfScale}
            isActive={isActiveStyle}
            style={{ opacity }}
          >
            <StyleIcon
              error={status === "layerError"}
              warn={status === "tableError"}
              type={type}
              geometryType={geometryType as never}
              sourceType={sourceType}
              style={style}
              symbol={originalSymbol}
              hasRasters={symbolHasRasters}
              hasClassification={symbolIsClassified}
            />
          </LayerIconContainer>
        ),
      [
        geometryType,
        isActiveStyle,
        isOutOfScale,
        noIcon,
        opacity,
        openClassification,
        originalSymbol,
        sourceType,
        status,
        style,
        symbolHasRasters,
        symbolIsClassified,
        t,
        type,
      ],
    );

    return (
      <>
        <Main
          $isExpanding={isExpanding}
          $isVisible={!noControl || isVisible}
          $noControl={noControl}
          $noVisibilityClick={noVisibilityClick}
        >
          {isSelectable && <Checkbox checked={isSelected} onChange={() => toggleSelected(name)} />}
          <IconContainer>{renderIcon}</IconContainer>
          <Section>
            <Content>
              <ItemTitle
                isLegendExpanded={expanded}
                toggleLegend={toggleExpanded}
                layer={layer}
                path={path}
                table={table}
                status={status}
                hasClassification={symbolIsClassified}
                projectLayer={projectLayer}
                halfVisible={isOutOfScale}
                noControl={noControl}
                noLegend={noLegend}
                noVisibilityClick={noVisibilityClick}
              />
              {isTemp && <TempLayerBadge />}
              {!noControl && (
                <Control>
                  {isErrorStatus(status) ? (
                    <RefreshButton layer={layer} table={table} refetch={refetch} />
                  ) : (
                    <>
                      {!isShared && !isRaster && isEditableLayer && isEditableType && !noMenu && (
                        <FeatureCreateButton
                          itemName={name}
                          geometryType={geometryType as never}
                          disabled={loading || !isVisible || isOutOfScale}
                          isLayerVisible={isVisible && !isOutOfScale}
                        />
                      )}
                      {(!["PostgresLayerService", "QueryLayerService"].includes(type) ||
                        geometryType !== GeometryType.Unknown) && (
                        <VisibilityButton
                          item={{ name, children, isVisible }}
                          disabled={loading}
                          isEditableLayer={isEditableLayer}
                        />
                      )}
                    </>
                  )}
                  {!noMenu && (
                    <ListMenu
                      layer={layer}
                      opacity={opacity}
                      table={table}
                      status={status}
                      loading={loading}
                      name={name}
                      isVisibleItem={isVisible}
                      isTemp={isTemp}
                      isOutOfScale={isOutOfScale}
                      isDragging={isDragging}
                      hasRasters={symbolHasRasters}
                    />
                  )}
                </Control>
              )}
            </Content>
          </Section>
        </Main>
        {!noLegend && expanded && !isEmpty(layer) && !isErrorStatus(status) && symbolIsClassified && (
          <StyledMapLegend>
            <Legend
              layerInfo={layer as FeatureLayerServiceInfoDc}
              hiddenLegends={hiddenLegends}
              toggleHiddenLegend={toggleHiddenLegend}
              clearHiddenLegends={clearHiddenLegends}
              goToClassification={() => goToStyle({ layer: layer.name })}
              formatValue={formatAttributeValue}
              isShared={isShared}
            />
          </StyledMapLegend>
        )}
      </>
    );
  },
);
